import isEqual from 'lodash/isEqual';
import { cloneDeep } from '../../libs/cloneDeep';
import type {
    TextDirection,
    TranscriptData,
    TranscriptWordData
} from '../../video-data/types';
import { Config } from '../../video-data/config';
import type { BaseSettings } from '../TimeAwareBase';
import { TimeInvariantBase } from '../TimeInvariantBase';
import type { DeepReadonlyObject } from '../../types';
import { assertIsValidTranscriptData } from '../../validators';


/**
 * Transcript
 * The transcript contains word timing information.
 */
export class Transcript extends TimeInvariantBase {
    #words: TranscriptWordData[] = [];
    #language: string = '';
    #textDirection: TextDirection = Config.LEFT_TO_RIGHT_TEXT_DIRECTION;
    #separateWordsWithSpaces: boolean = true;

    constructor(settings: BaseSettings = { fetchResources: true }) {
        super(settings);
    }

    get empty() {
        return this.#words.length === 0;
    }

    /** returns basic transcript word statistics: minimum, maximum, mean and average word duration **/
    getWordDurationStats() {
        let min = Infinity;
        let max = -Infinity;
        let average = 0;
        const durations = [];
        this.words.forEach(word => {
            const duration = word.endTime - word.startTime;
            if (min > duration) {
                min = duration;
            }
            if (max < duration) {
                max = duration;
            }
            average += duration;
            durations.push(duration);
        });
        average /= this.words.length;
        const mean = durations[Math.round((this.words.length - 1) / 2)];
        return { min, max, average, mean };
    }

    /**
     * duration (in milliseconds)
     */
    get duration(): number {
        if (this.#words.length === 0) {
            return 0;
        }
        return (this.endTime - this.startTime);
    }

    get words(): DeepReadonlyObject<TranscriptWordData>[] {
        return this.#words;
    }

    get firstWord(): DeepReadonlyObject<TranscriptWordData> | undefined {
        return this.#words[0];
    }

    get lastWord(): DeepReadonlyObject<TranscriptWordData> | undefined {
        return this.#words.at(-1);
    }

    get text(): string {
        return this.#words.map(transcriptWordData => transcriptWordData.word).join(this.separateWordsWithSpaces ? ' ' : '');
    }

    get textDirection(): TextDirection {
        return this.#textDirection;
    }

    /**
     * Transcript language (ISO code)
     */
    get language(): string {
        return this.#language;
    }

    /**
     * start time (in milliseconds)
     */
    get startTime(): number | undefined {
        return this.#words[0]?.startTime;
    }

    get separateWordsWithSpaces(): boolean {
        return this.#separateWordsWithSpaces;
    }

    /**
     * end time (in milliseconds)
     */
    get endTime(): number | undefined {
        return this.#words.at(-1)?.endTime;
    }

    /**
     * Get active word index at time (in milliseconds).
     * A word is considered active when the given time is greater than or equal to the word's start time and
     * less than the next word's start time.
     */
    getActiveWordIndexAtTime(time: number): number {
        if (time < this.startTime) {
            return -1;
        }
        if (time > this.endTime) {
            return -1;
        }
        return this.#words.findIndex((word, index) => {
            if (index < this.#words.length - 1) {
                return word.startTime <= time && time < this.#words[index + 1].startTime;
            }
            return word.startTime <= time;
        });
    }

    override get propertiesToSerialize(): string[] {
        return [
            'words',
            'language',
            'textDirection',
            'separateWordsWithSpaces',
        ];
    }

    set transcriptData(data: TranscriptData) {
        assertIsValidTranscriptData(data);

        if (isEqual(data, this.transcriptData)) {
            return;
        }
        this.#words = cloneDeep(data.words);
        this.#language = data.language;
        this.#textDirection = data.textDirection;
        this.#separateWordsWithSpaces = true;

        if (data.hasOwnProperty('separateWordsWithSpaces')) {
            this.#separateWordsWithSpaces = data.separateWordsWithSpaces;
        }

        this.requiresUpdate = true;
    }

    get transcriptData(): TranscriptData {
        return {
            words: this.#words,
            language: this.#language,
            textDirection: this.#textDirection,
            separateWordsWithSpaces: this.#separateWordsWithSpaces,
        };
    }

    _wordsCustomLoad(words: TranscriptWordData[]): void {
        this.#words = cloneDeep(words);
    }

    _wordsCustomSave(): TranscriptWordData[] {
        return cloneDeep(this.#words);
    }

    _languageCustomLoad(language: string): void {
        this.#language = language;
    }

    _textDirectionCustomLoad(textDirection: TextDirection): void {
        this.#textDirection = textDirection;
    }

    _separateWordsWithSpacesCustomLoad(separateWordsWithSpaces: boolean): void {
        this.#separateWordsWithSpaces = separateWordsWithSpaces;
    }

    override clear() {
        super.clear();
        this.#words = [];
        this.#language = '';
        this.#textDirection = Config.LEFT_TO_RIGHT_TEXT_DIRECTION;
        this.#separateWordsWithSpaces = true;
    }

    getDataForRange(startTime: number, endTime: number) {
        return {
            words: this.getWordsWithinRange(startTime, endTime),
            language: this.language,
            textDirection: this.textDirection,
            separateWordsWithSpaces: this.separateWordsWithSpaces,
        } as TranscriptData;
    }

    /**
     * Subtracts the offset time from all word timings in the transcript.
     * @deprecated Use delayWords instead.
     **/
    offsetWords(offsetTime: number) {
        this.delayWords(-offsetTime);
    }

    /**
     * Delay all word times in the transcript by the provided number of milliseconds.
     **/
    delayWords(delay: number) {
        this.words.forEach(word => {
            (word.startTime as number) += delay;
            (word.endTime as number) += delay;
        });
        this.requiresUpdate = true;
    }

    /**
     * Returns transcript data trimmed to start and end time.
     */
    getTrimmedTranscriptData(startTime: number, endTime: number): TranscriptData {
        return {
            words: this.getWordsWithinRange(startTime, endTime),
            language: this.language,
            textDirection: this.textDirection,
            separateWordsWithSpaces: this.separateWordsWithSpaces,
        } as TranscriptData;
    }

    /**
     * Returns a new transcript trimmed to start and end time.
     */
    getTrimmedTranscript(startTime: number, endTime: number): Transcript {
        const transcriptData = this.getTrimmedTranscriptData(startTime, endTime);
        const newTranscript = new Transcript({ fetchResources: true });
        newTranscript.transcriptData = transcriptData;
        return newTranscript;
    }


    /**
     * Returns the words that are active at the given time and mirrors the transcript editing behaviour from Web. It's
     * crucial that this method is kept in sync. Otherwise, the transcript editing behaviour will be different between
     * Web (transcript editor) and Luminary (previews).
     * Only full words are included in this range, if a time boundary is partway through a word, it is not included.
     **/
    getWordsWithinRange(startTime: number, endTime: number) {
        if (this.empty) {
            return [];
        }
        if (startTime > this.endTime || endTime < this.startTime) {
            return [];
        }

        let startIndex = startTime < this.startTime ?
            0 :
            this.getActiveWordIndexAtTime(startTime);
        const startWord = this.#words[startIndex];
        if (startWord.startTime < startTime) {
            // ensure that the start word is not partway through
            startIndex++;
        }

        let endIndex = endTime > this.endTime ?
            this.#words.length - 1 :
            this.getActiveWordIndexAtTime(endTime);
        const endWord = this.#words[endIndex];
        if (endWord.endTime > endTime) {
            // ensure that the end word is not partway through
            endIndex--;
        }

        return [...this.#words.slice(startIndex, endIndex + 1)];
    }

    generateWordsFromTranscript() {
        const words = this.words
            .filter(transcriptWordData => transcriptWordData.word !== '')
            .flatMap(transcriptWordData => transcriptWordData.word);

        let wordsWithSpaces = [];
        let sourceWordReferenceIndices = null;

        if (this.separateWordsWithSpaces) {
            // In English and other languages with spaces: add space after each word
            // and build sourceWordReferenceIndices according to these spaces.
            // create a list of indices that reference the word list and add -1 to indicate a space
            sourceWordReferenceIndices = new Array(words.length * 2);
            wordsWithSpaces = words.flatMap(word => [word, ' ']);
            wordsWithSpaces.pop(); // remove the last space

            for (let i = 0; i < words.length; i++) {
                sourceWordReferenceIndices[i * 2] = i;
                sourceWordReferenceIndices[i * 2 + 1] = -1;
            }
        }
        else {
            // Some languages (Thai, Japanese, Javanese, Chinese, etc.)
            // don't have spaces between words.
            // In this case, sourceWordReferenceIndices a direct mapping of index to word.
            sourceWordReferenceIndices = new Array(words.length);
            wordsWithSpaces = words.flatMap(word => [word]);
            for (let i = 0; i < words.length; i++) {
                sourceWordReferenceIndices[i] = i;
            }
        }

        return { wordsWithSpaces, sourceWordReferenceIndices };
    }
}
